import React from 'react';
import { Menu, Popover, Tooltip } from 'freemium-ui';
import { History } from 'history';
import { useTranslation } from 'react-i18next';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useAccessControl } from 'utils/access-control';
import appConfig from 'utils/config';
import { useGetAccountInfo } from '../../hooks';
import { SidebarIcon, StyledSidebar, ThemedMenu } from './styles';
import { helpOptions, routes } from './constants';
import Icon from '../Icon';

const Sidebar = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { push }: History = useHistory();
  const [anchorEl, setAnchorEl] = React.useState<any>(null);
  const [openMenu, setOpenMenu] = React.useState<string>('');
  const { data: accountInfo } = useGetAccountInfo({});
  const { access } = useAccessControl();

  const tHelpOptions = helpOptions.map((option) => ({
    ...option,
    name: `${t(option.name)}`,
  }));

  const accessControlledRoutes = React.useMemo(
    () =>
      routes.filter((route: any) => {
        switch (route.id) {
          case 'dashboard':
            return (access || []).indexOf('dashboard:read') > -1;
          case 'response':
            return (access || []).indexOf('response:read') > -1;
          case 'survey':
            return (access || []).indexOf('survey:read') > -1;
          case 'queue':
            return (access || []).indexOf('queue:read') > -1;
          case 'workflow':
            return (access || []).indexOf('workflow:read') > -1;
          case 'settings':
            return (access || []).indexOf('settings:read') > -1;
          case 'contact':
            return (access || []).indexOf('contact:read') > -1;
          case 'analytics':
            return (access || []).indexOf('analytics:read') > -1;
          default:
            return true;
        }
      }),
    [access],
  );

  return (
    <StyledSidebar>
      <div className="item fsurvey" onClick={() => push('/')}>
        <Icon height="36" width="36" glyph="fsurvey" />
      </div>
      {accessControlledRoutes.map((route) =>
        route.path ? (
          <Tooltip label={`${t(route.name)}`} position="right" key={route.id}>
            <Link
              aria-label={`${t(route.name)}`}
              to={route.path}
              className="item"
              role="button"
              tabIndex={0}
              onMouseOver={() => setOpenMenu('')}
            >
              <SidebarIcon
                className={`icon-holder ${
                  pathname.startsWith(route.path) ? 'active' : ''
                }`.trim()}
              >
                <Icon
                  glyph={route.icon}
                  id={route.id}
                  viewBox="0 0 20 20"
                  fill="transparent"
                  {...route.iconAttributes}
                />
              </SidebarIcon>
            </Link>
          </Tooltip>
        ) : (
          <React.Fragment key={route.id}>
            <div
              key={route.id}
              role="button"
              tabIndex={0}
              // to={route.paths[0].path}
              className="item"
              onMouseOver={() => setOpenMenu(route.id)}
              onFocus={() => setOpenMenu(route.id)}
              aria-label={`${route.id}`}
            >
              <SidebarIcon
                className={`icon-holder ${
                  pathname === route.path ||
                  anchorEl?.href === `${location.origin}/contacts` ||
                  anchorEl?.href === `${location.origin}/response` ||
                  route.paths.some((v: any) => pathname.indexOf(v.path) >= 0)
                    ? 'active'
                    : ''
                }`.trim()}
              >
                <Icon
                  id={route.id}
                  glyph={route.icon}
                  viewBox="0 0 20 20"
                  fill="transparent"
                  {...route.iconAttributes}
                />
              </SidebarIcon>
              {route.id === openMenu && (
                <ThemedMenu onMouseLeave={() => setOpenMenu('')}>
                  <Menu
                    options={
                      // { id: route.id, nam.e: route.name, disabled: true },
                      route.paths.map((path) => ({
                        ...path,
                        name: `${t(path.name)}`,
                      }))
                    }
                    onSelect={({ path }: any) => push(path)}
                    closeMenuCallback={() => setOpenMenu('')}
                  />
                </ThemedMenu>
              )}
            </div>
          </React.Fragment>
        ),
      )}
      <React.Fragment>
        <div
          style={{ position: 'absolute', bottom: '8%' }}
          onMouseOver={(e) => setAnchorEl(e.currentTarget)}
          onFocus={(e) => setAnchorEl(e.currentTarget)}
          aria-describedby="modal-dd-help-widget"
        >
          <Icon width="24" height="24" glyph={'help'} />
        </div>
        <Popover
          id="modal-dd-help-widget"
          anchorEl={anchorEl}
          hoverOutToClose
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <div>
            <Menu
              options={tHelpOptions}
              onSelect={({ path }: any) => window.open(path)}
              closeMenuCallback={() => setOpenMenu('')}
            ></Menu>
          </div>
        </Popover>
      </React.Fragment>
      <freshworks-omnibar
        data-omnibar-product="freshsurvey"
        data-omnibar-environment={appConfig.FRESHID_ENV}
        data-omnibar-user-role="admin"
        data-omnibar-user-locale="en-us"
        data-omnibar-organisation-domain={accountInfo?.organisation_domain}
        data-omnibar-client-id={appConfig.CLIENT_ID}
        data-omnibar-session-state={window.localStorage.getItem(
          'freshid-session-state',
        )}
        data-omnibar-session-token={window.localStorage.getItem(
          'freshid-session-token',
        )}
        data-omnibar-product-logout-url={window.location.host}
      ></freshworks-omnibar>
    </StyledSidebar>
  );
};
export default Sidebar;
